/**
 * Moments in the video, in the business's words (section 4): a class's moves (numbered into rounds
 * by their order), a concert's setlist, a match's key moments with the score, a service's order.
 * Times are minutes and seconds ("12:30"), set by typing or from the player ("Start here"); ids are
 * made, never typed. A live event's timeline counts from its start.
 */

import { useState } from "react";
import Button from "@web-ui/components/Button";
import Field from "@web-ui/components/Field";
import Notice from "@web-ui/components/Notice";
import { useHls } from "@/media/use-hls";
import { useUploads } from "@/media/upload-center";
import { useForm } from "../form-context";
import { byStart, cleanMarker, nextId, withRounds, type Marker } from "../moments";
import { fieldDomId, getPath } from "../paths";
import type { SectionPlan } from "../plan";
import { clock } from "../time";
import { OptionSelect } from "../controls/field-control";
import { ClockField } from "../controls/inputs";

type Section = Extract<SectionPlan, { id: "moments" }>;

/** The video the moments sit on: a title's own, or a live event's replay or recording. */
function useSource(path: string): { src: string | null; length: number | null } {
  const form = useForm();
  const center = useUploads();
  const refPath = path === "timeline" ? (getPath(form.doc, "replay.video.videoId") ? "replay.video" : "live.video") : "playback.video";
  const ref = getPath(form.doc, refPath) as { host?: string; videoId?: string } | undefined;
  if (!ref?.videoId) return { src: null, length: null };
  if (ref.host === "url") return { src: ref.videoId, length: null };
  const asset = center.asset(ref.videoId);
  return { src: asset?.manifestUrl ?? null, length: (asset?.details as { durationSeconds?: number } | null)?.durationSeconds ?? null };
}

export function MomentsSection({ section }: { section: Section }) {
  const form = useForm();
  const markers = ((getPath(form.doc, section.path) as Marker[] | undefined) ?? []);
  // A recipe's step clips are kept with its steps; this section shows the moments of its own kinds.
  const kinds = section.kinds.map((k) => k.kind);
  const mine = markers.map((m, index) => ({ m, index })).filter((x) => kinds.includes(x.m.kind)).sort((a, b) => byStart(a.m, b.m));
  const { src, length: assetLength } = useSource(section.path);
  const { ref, error } = useHls(src);
  const [now, setNow] = useState(0);
  const [kind, setKind] = useState(section.kinds[0]!.kind);
  const length = assetLength ?? (form.doc["durationSeconds"] as number | undefined) ?? (section.path === "timeline" ? spanOf(form.doc) : null) ?? 600;
  const word = (k: string) => section.kinds.find((x) => x.kind === k)?.word ?? k;

  const write = (next: Marker[]) => {
    const cleaned = (section.rounds ? withRounds(next) : next).map(cleanMarker).sort(byStart);
    form.set(section.path, cleaned);
  };
  const change = (index: number, patch: Partial<Marker>) => write(markers.map((m, i) => (i === index ? { ...m, ...patch } : m)));
  const add = () => write([...markers, { id: nextId(kind, markers.map((m) => m.id)), kind, startSeconds: Math.round(now) }]);
  const seek = (s: number) => { if (ref.current) { ref.current.currentTime = s; setNow(s); } };

  // The feed keeps a video's moments with the video: a title marks them once it has one.
  if (section.path !== "timeline" && !getPath(form.doc, "playback.video.videoId")) {
    return <p className="text-body-2 text-t-secondary">{`Add the video first (above): the ${section.title.toLowerCase()} are marked on it.`}</p>;
  }

  return (
    <div className="flex flex-col gap-4">
      {src ? (
        <video ref={ref} controls className="aspect-video w-full max-w-2xl rounded-2xl bg-b-dark1" onTimeUpdate={(e) => setNow(e.currentTarget.currentTime)} />
      ) : (
        <p className="text-body-2 text-t-secondary">{section.path === "timeline" ? "Once there is a replay or a recording, it plays here so you can mark moments as you watch." : "Once the video is ready it plays here, so you can mark moments as you watch. You can type times in already."}</p>
      )}
      {error ? <Notice tone="warning">{error}</Notice> : null}

      {src || mine.length ? <div className="relative h-8 w-full max-w-2xl rounded-xl bg-b-surface1" aria-hidden="true">
        {mine.map(({ m }) => (
          <span key={m.id} className="absolute top-1 bottom-1 min-w-1 rounded bg-accent opacity-80" style={{ left: `${Math.min(100, (m.startSeconds / length) * 100)}%`, width: m.endSeconds ? `${Math.max(0.5, ((m.endSeconds - m.startSeconds) / length) * 100)}%` : undefined }} />
        ))}
        {src ? <span className="absolute top-0 bottom-0 w-0.5 bg-t-primary" style={{ left: `${Math.min(100, (now / length) * 100)}%` }} /> : null}
      </div> : null}

      {mine.length ? (
        <ol className="flex flex-col gap-2">
          {mine.map(({ m, index }, position) => {
            const base = `${section.path}.${index}`;
            return (
              <li key={m.id} className="flex flex-col gap-3 rounded-2xl bg-b-surface1 p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sub-title-2">{section.rounds ? `${word(m.kind)} ${position + 1} of ${mine.length}` : `${word(m.kind)} ${position + 1}`}</span>
                  <span className="grow" />
                  {src ? <Button type="button" size="xs" variant="ghost" onClick={() => seek(m.startSeconds)}>{`Play from ${clock(m.startSeconds)}`}</Button> : null}
                  {form.canEdit ? <Button type="button" size="xs" variant="ghost" icon="close" isIconOnly aria-label={`Remove ${word(m.kind).toLowerCase()} ${position + 1}`} onClick={() => write(markers.filter((_, i) => i !== index))} /> : null}
                </div>
                <div className={`grid items-start gap-2 ${section.timer && m.kind === "move" ? "md:grid-cols-[minmax(0,1fr)_7rem_7rem_7rem]" : "md:grid-cols-[minmax(0,1fr)_7rem_7rem]"}`}>
                  <Field id={fieldDomId(`${base}.title`)} label="Name" size="sm" placeholder={m.kind === "move" ? "e.g. Low lunge" : m.kind === "song" ? "Song title" : "Name"} value={m.title?.[form.locale] ?? ""} disabled={!form.canEdit}
                    onChange={(e) => change(index, { title: { ...(m.title ?? {}), [form.locale]: e.target.value } })} />
                  <ClockField id={fieldDomId(`${base}.startSeconds`)} label="From" value={m.startSeconds} error={form.issue(`${base}.startSeconds`)?.message} onChange={(v) => change(index, { startSeconds: v ?? 0 })} />
                  <ClockField id={fieldDomId(`${base}.endSeconds`)} label="To" optional value={m.endSeconds} error={form.issue(`${base}.endSeconds`)?.message} onChange={(v) => { change(index, { endSeconds: v }); form.touch(`${base}.endSeconds`); }} />
                  {section.timer && m.kind === "move" ? <ClockField label="Timer" optional placeholder="None" value={typeof m.data?.["timerSeconds"] === "number" ? (m.data["timerSeconds"] as number) : undefined} onChange={(v) => change(index, { data: { ...(m.data ?? {}), timerSeconds: v === undefined ? "" : Math.round(v) } })} /> : null}
                </div>
                {section.kinds.length > 1 ? <div className="max-w-60"><OptionSelect label="What it is" value={m.kind} options={section.kinds.map((k) => ({ value: k.kind, label: k.word }))} disabled={!form.canEdit} onChange={(v) => change(index, { kind: v ?? m.kind })} /></div> : null}
                {section.score && m.kind === "key-moment" ? (
                  <div className="grid gap-2 sm:grid-cols-3">
                    <OptionSelect label="What happened" value={String(m.data?.["type"] ?? "") || undefined} allowNone options={[["goal", "Goal"], ["yellow-card", "Yellow card"], ["red-card", "Red card"], ["substitution", "Substitution"], ["penalty", "Penalty"], ["try", "Try"]].map(([v, l]) => ({ value: v!, label: l! }))} disabled={!form.canEdit}
                      onChange={(v) => change(index, { data: { ...(m.data ?? {}), type: v ?? "" } })} />
                    <Field label="Home score after" size="sm" type="number" min={0} optional value={m.data?.["home"] === undefined ? "" : String(m.data["home"])} disabled={!form.canEdit}
                      onChange={(e) => change(index, { data: { ...(m.data ?? {}), home: e.target.value === "" ? "" : Number(e.target.value) } })} />
                    <Field label="Away score after" size="sm" type="number" min={0} optional value={m.data?.["away"] === undefined ? "" : String(m.data["away"])} disabled={!form.canEdit}
                      onChange={(e) => change(index, { data: { ...(m.data ?? {}), away: e.target.value === "" ? "" : Number(e.target.value) } })} />
                  </div>
                ) : null}
                {form.canEdit && src ? (
                  <div className="flex flex-wrap gap-1">
                    <Button type="button" size="xs" variant="ghost" onClick={() => change(index, { startSeconds: Math.round(now) })}>{`Start at ${clock(now)}`}</Button>
                    <Button type="button" size="xs" variant="ghost" onClick={() => change(index, { endSeconds: Math.round(now) })}>{`End at ${clock(now)}`}</Button>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ol>
      ) : <p className="text-body-2 text-t-secondary">{`No ${section.title.toLowerCase()} yet.`}</p>}

      {form.canEdit ? (
        <div className="flex flex-wrap items-end gap-2">
          {section.kinds.length > 1 ? <div className="w-48"><OptionSelect label="Add" value={kind} options={section.kinds.map((k) => ({ value: k.kind, label: k.word }))} onChange={(v) => setKind(v ?? kind)} /></div> : null}
          <Button type="button" size="sm" variant="secondary" icon="plus" onClick={add}>{src ? `Add a ${word(kind).toLowerCase()} at ${clock(now)}` : `Add a ${word(kind).toLowerCase()}`}</Button>
        </div>
      ) : null}
    </div>
  );
}

function spanOf(doc: Record<string, unknown>): number | null {
  const s = Date.parse(String(doc["startsAt"] ?? "")), e = Date.parse(String(doc["endsAt"] ?? ""));
  return Number.isNaN(s) || Number.isNaN(e) || e <= s ? null : (e - s) / 1000;
}
